import { motion } from "framer-motion";
import {
    AlertTriangle,
    ArrowRight,
    Briefcase,
    CheckCircle2,
    ClipboardCheck,
    Flame,
    Inbox,
    Mail,
    MapPin,
    Phone,
    Sparkles,
    TrendingUp,
    Users,
    Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import KpiTile from "../components/dashboard/KpiTile";
import ChannelPerformance from "../components/dashboard/ChannelPerformance";
import RelancesPanel from "../components/dashboard/RelancesPanel";
import { DailyImmersionsSelfTracker, DailyImmersionsTeamTracker } from "../components/dashboard/DailyImmersionsTracker";
import ScoreRing from "../components/candidates/ScoreRing";
import SmartBadge from "../components/candidates/SmartBadge";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { liveSignal, timeAgo, toE164 } from "../lib/format";
import { hasRole } from "../lib/roles";

function MiniCandidateRow({ candidate, accent = "orange" }) {
    const phone = toE164(candidate.phone);
    return (
        <div className="flex items-center gap-3 p-3 rounded-2xl hover:bg-shariti-gray transition group">
            <Link to={`/candidats/${candidate.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                {candidate.avatar_url ? (
                    <img src={candidate.avatar_url} alt="" className="h-10 w-10 rounded-xl object-cover" />
                ) : (
                    <div className="h-10 w-10 rounded-xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-sm flex items-center justify-center">
                        {candidate.first_name?.[0]}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-extrabold text-shariti-black truncate">
                        {candidate.first_name} {candidate.last_name}
                    </div>
                    <div className="text-xs font-medium text-slate-500 truncate">
                        {candidate.city} · {candidate.phone}
                    </div>
                </div>
                <div className={`text-sm font-black ${accent === "red" ? "text-shariti-red" : "text-shariti-orange"}`}>
                    {candidate.ai_score || "—"}
                </div>
            </Link>
            <a
                href={`tel:${phone}`}
                title="Appeler"
                onClick={(e) => e.stopPropagation()}
                className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-shariti-orange text-white hover:bg-[#d66528] transition shadow-btn"
            >
                <Phone className="h-3.5 w-3.5" />
            </a>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [feed, setFeed] = useState(null);
    const [callsSummary, setCallsSummary] = useState(null);
    const [emailKpis, setEmailKpis] = useState(null);
    const [scopeActive, setScopeActive] = useState(true);

    useEffect(() => {
        async function load() {
            const params = { scope: scopeActive ? "active" : "all" };
            const [ov, fd] = await Promise.all([
                api.get("/dashboard/overview", { params }),
                api.get("/dashboard/feed", { params }),
            ]);
            setOverview(ov.data);
            setFeed(fd.data);
        }
        load();
        // Cockpit tile "Analytics Appels" — 3 numbers of the day.
        // Fails silently if the user doesn't have the role (referents).
        api.get("/analytics/calls/cockpit-summary").then(({ data }) => {
            setCallsSummary(data);
        }).catch(() => setCallsSummary(null));
        // Cockpit tile "Délivrabilité emails" — admin-only tile that
        // aggregates Resend webhook events (delivered / opened / bounced)
        // over the last 30 days. Silently 403 for non-admins.
        api.get("/emails/kpis", { params: { days: 30 } }).then(({ data }) => {
            setEmailKpis(data);
        }).catch(() => setEmailKpis(null));
    }, [scopeActive]);

    const urgentCount = overview?.urgent_calls ?? 0;
    const immersionsUpcoming = overview?.immersions_upcoming ?? 0;
    const awaitingValidation = overview?.awaiting_validation ?? 0;
    const topPepite = feed?.pepites?.[0];
    const firstName = user?.name?.split(" ")[0] || "";
    const isReferent = hasRole(user, "referent");
    const isPreRecruteur = hasRole(user, "pre_recruteur");

    // Role-specific hero copy
    const heroCount = isReferent
        ? (immersionsUpcoming + awaitingValidation)
        : urgentCount;
    const heroLabel = isReferent
        ? "immersion" + (heroCount > 1 ? "s" : "") + " à évaluer"
        : "candidat" + (heroCount > 1 ? "s" : "") + " à appeler maintenant";
    const heroCta = isReferent
        ? { to: "/candidats?status=immersion_scheduled", label: "Voir mes immersions" }
        : { to: "/candidats?status=urgent", label: "Voir la file d'appel" };
    const heroSubtitle = isReferent
        ? "Évaluez les candidats sur le terrain, validez leur performance et recommandez leur recrutement."
        : "Priorisez les meilleurs profils, scorez-les en live et déclenchez l'immersion en quelques secondes.";

    return (
        <div className="space-y-8" data-testid="dashboard">
            {/* Hero */}
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#181818] via-[#231915] to-[#3b2010] text-white p-7 md:p-10"
            >
                <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-shariti-orange/30 blur-3xl" />
                <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-shariti-yellow/20 blur-3xl" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-white/20">
                            <Sparkles className="h-3 w-3" /> Cockpit recrutement terrain
                        </div>
                        <h1 className="mt-4 text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
                            Bonjour {firstName},<br />
                            {heroCount > 0 ? (
                                <>
                                    <span className="text-shariti-orange">{heroCount}</span>{" "}
                                    {heroLabel}.
                                </>
                            ) : isReferent ? (
                                <>aucune immersion en attente.</>
                            ) : (
                                <>aucun appel urgent en attente.</>
                            )}
                        </h1>
                        <p className="mt-3 text-white/70 font-medium max-w-xl">
                            {heroSubtitle}
                        </p>
                        <div className="mt-4 flex items-center gap-3">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={scopeActive}
                                    onChange={(e) => setScopeActive(e.target.checked)}
                                    data-testid="dashboard-scope-toggle"
                                    className="h-4 w-4 rounded accent-shariti-orange"
                                />
                                <span className="text-[11px] font-bold uppercase tracking-widest text-white/80">
                                    Offres actives uniquement
                                </span>
                            </label>
                        </div>
                        {heroCount > 0 && (
                            <Link
                                to={heroCta.to}
                                data-testid="hero-urgent-cta"
                                className="mt-5 inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white font-bold px-5 py-2.5 text-sm shadow-btn hover:bg-[#d66528] transition"
                            >
                                {isReferent ? <MapPin className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                                {heroCta.label}
                            </Link>
                        )}
                    </div>
                    {topPepite ? (
                        <Link
                            to={`/candidats/${topPepite.id}`}
                            data-testid="hero-top-pepite"
                            className="flex items-center gap-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-5 py-4 hover:bg-white/15 transition"
                        >
                            <ScoreRing score={topPepite.ai_score} size={68} stroke={5} label="" />
                            <div className="text-left">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-white/60">
                                    Top pépite
                                </div>
                                <div className="text-base font-extrabold leading-tight">
                                    {topPepite.first_name} {topPepite.last_name}
                                </div>
                                <div className="text-xs font-medium text-white/60">
                                    {topPepite.city} · Postulé {timeAgo(topPepite.applied_at || topPepite.created_at)}
                                </div>
                            </div>
                            <ArrowRight className="h-4 w-4 text-white/60" />
                        </Link>
                    ) : null}
                </div>
            </motion.div>

            {/* Analytics Appels — cockpit tile (only for roles that see it) */}
            {callsSummary && (isPreRecruteur || !isReferent) && (
                <Link
                    to="/analytics-appels"
                    data-testid="cockpit-analytics-tile"
                    className="group flex items-center gap-4 rounded-2xl bg-white border-2 border-slate-100 hover:border-shariti-orange/40 hover:shadow-lg p-4 transition"
                >
                    <div className="h-11 w-11 rounded-2xl bg-shariti-orange-soft text-shariti-orange inline-flex items-center justify-center shrink-0">
                        <Phone className="h-5 w-5" strokeWidth={2.4} />
                    </div>
                    <div className="flex-1 min-w-0 grid grid-cols-3 gap-3">
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Appels · aujourd&apos;hui
                            </div>
                            <div className="text-2xl font-black text-shariti-black leading-none mt-0.5">
                                {callsSummary.calls_today}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Taux décroché
                            </div>
                            <div className="text-2xl font-black text-emerald-600 leading-none mt-0.5">
                                {callsSummary.reach_rate_today}
                                <span className="text-base text-slate-400 ml-0.5">%</span>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Qualifiés
                            </div>
                            <div className="text-2xl font-black text-shariti-orange leading-none mt-0.5">
                                {callsSummary.qualified_today}
                            </div>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-shariti-orange transition shrink-0">
                        Analytics Appels
                        <ArrowRight className="w-3 h-3" />
                    </div>
                </Link>
            )}

            {/* Deliverability KPIs — admin cockpit tile that surfaces the
                open / bounce rates over the last 30 days so the CEO
                knows at a glance if Resend is healthy. */}
            {emailKpis && emailKpis.sent > 0 && (
                <Link
                    to="/admin/emails"
                    data-testid="cockpit-deliverability-tile"
                    className="group flex items-center gap-4 rounded-2xl bg-white border-2 border-slate-100 hover:border-shariti-orange/40 hover:shadow-lg p-4 transition"
                >
                    <div className="h-11 w-11 rounded-2xl bg-shariti-orange-soft text-shariti-orange inline-flex items-center justify-center shrink-0">
                        <Inbox className="h-5 w-5" strokeWidth={2.4} />
                    </div>
                    <div className="flex-1 min-w-0 grid grid-cols-3 gap-3">
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Emails · 30j
                            </div>
                            <div className="text-2xl font-black text-shariti-black leading-none mt-0.5" data-testid="deliverability-sent">
                                {emailKpis.sent}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Taux ouverture
                            </div>
                            <div className="text-2xl font-black text-emerald-600 leading-none mt-0.5" data-testid="deliverability-open-rate">
                                {Math.round((emailKpis.open_rate || 0) * 100)}
                                <span className="text-base text-slate-400 ml-0.5">%</span>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Taux bounce
                            </div>
                            <div
                                className={`text-2xl font-black leading-none mt-0.5 ${
                                    (emailKpis.bounce_rate || 0) > 0.05 ? "text-red-600" : "text-shariti-black"
                                }`}
                                data-testid="deliverability-bounce-rate"
                            >
                                {Math.round((emailKpis.bounce_rate || 0) * 100)}
                                <span className="text-base text-slate-400 ml-0.5">%</span>
                            </div>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-shariti-orange transition shrink-0">
                        Historique emails
                        <ArrowRight className="w-3 h-3" />
                    </div>
                </Link>
            )}

            {/* Daily immersions objective — PRIORITY display.
                Self tracker for pre-recruteurs (always on top so they see
                their goal as soon as they land). Team tracker for admins
                (visibility on the whole team). */}
            {isPreRecruteur && (
                <DailyImmersionsSelfTracker firstName={user?.name?.split(" ")[0]} />
            )}
            {!isReferent && !isPreRecruteur && (
                <DailyImmersionsTeamTracker />
            )}

            {/* KPIs — toutes cliquables */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {!isReferent && (
                    <KpiTile
                        label="Pépites"
                        value={overview?.pepites ?? 0}
                        icon={Flame}
                        tone="orange"
                        testId="kpi-pepites"
                        hint="Score IA ≥ 82"
                        to="/pepites"
                    />
                )}
                {!isReferent && (
                    <KpiTile
                        label="Appels urgents"
                        value={urgentCount}
                        icon={Zap}
                        tone="dark"
                        testId="kpi-urgent"
                        hint="À contacter aujourd'hui"
                        to="/candidats?status=urgent"
                    />
                )}
                <KpiTile
                    label={isReferent ? "Immersions à évaluer" : "Immersions à venir"}
                    value={overview?.immersions_upcoming ?? 0}
                    icon={MapPin}
                    tone={isReferent ? "orange" : "light"}
                    testId="kpi-immersions"
                    hint={isReferent ? "Programmées chez moi" : "Programmées"}
                    to="/candidats?status=immersion_scheduled"
                />
                <KpiTile
                    label={isReferent ? "Mes recommandations" : "Validation attente"}
                    value={overview?.awaiting_validation ?? 0}
                    icon={CheckCircle2}
                    tone="yellow"
                    testId="kpi-validation"
                    hint={isReferent ? "Immersions évaluées" : "Recommandations terrain"}
                    to="/candidats?status=immersion_done"
                />
                {!isPreRecruteur && (
                    <KpiTile
                        label="Onboarding"
                        value={overview?.onboarding ?? 0}
                        icon={Mail}
                        tone="green"
                        testId="kpi-onboarding"
                        hint="Emails envoyés"
                        to="/candidats?status=onboarding"
                    />
                )}
                <KpiTile
                    label="Profils à risque"
                    value={overview?.at_risk ?? 0}
                    icon={AlertTriangle}
                    tone="red"
                    testId="kpi-at-risk"
                    hint="Red flags actifs"
                    to="/alertes"
                />
            </div>

            {/* Insight strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
                <Link to="/candidats" className="card-soft p-5 col-span-1 hover:-translate-y-0.5 transition" data-testid="insight-vivier">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Vivier total</div>
                    <div className="mt-2 flex items-end gap-2">
                        <span className="text-4xl font-black tracking-tight text-shariti-black">{overview?.total ?? 0}</span>
                        <Users className="h-5 w-5 text-shariti-orange mb-1" />
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-500">candidats actifs</div>
                </Link>
                <div className="card-soft p-5 col-span-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Score moyen IA</div>
                    <div className="mt-2 flex items-end gap-2">
                        <span className="text-4xl font-black tracking-tight text-shariti-orange">{overview?.avg_score ?? 0}</span>
                        <TrendingUp className="h-5 w-5 text-shariti-orange mb-1" />
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-500">/ 100 — vivier total</div>
                </div>
                <div className="card-soft p-5 col-span-2">
                    {(() => {
                        // Show only the conversion metrics relevant to the user's role.
                        // - Pure pré-recruteur → Conversion pré-rec + Recrutement final
                        // - Pure référent → Conversion immersion + Recrutement final
                        // - Composite / admin → the 3 metrics
                        const isAdminUser = !isReferent && !isPreRecruteur ? true : (isReferent && isPreRecruteur);
                        const allMetrics = [
                            { id: "prerec", k: overview?.rate_prerec ?? 0, l: "Conversion pré-rec", color: "text-blue-600" },
                            { id: "immersion", k: overview?.rate_immersion ?? 0, l: "Conversion immersion", color: "text-violet-600" },
                            { id: "hired", k: overview?.rate_hired ?? 0, l: "Recrutement final", color: "text-emerald-600" },
                        ];
                        let metrics = allMetrics;
                        if (!isAdminUser) {
                            // user is a pure single-role pre_recruteur OR a pure single-role referent
                            if (isPreRecruteur && !isReferent) {
                                metrics = allMetrics.filter((m) => m.id !== "immersion");
                            } else if (isReferent && !isPreRecruteur) {
                                metrics = allMetrics.filter((m) => m.id !== "prerec");
                            }
                        }
                        const cols = metrics.length === 2 ? "grid-cols-2" : "grid-cols-3";
                        return (
                            <div className={`grid ${cols} gap-3`}>
                                {metrics.map((m) => (
                                    <div key={m.l} className="rounded-2xl bg-shariti-cream p-4">
                                        <div className={`text-2xl font-black tracking-tight ${m.color}`}>{m.k}%</div>
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mt-1">{m.l}</div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Channel performance (admin-only — strategic acquisition view) */}
            {!isReferent && !isPreRecruteur && (
                <ChannelPerformance scope={scopeActive ? "active" : "all"} />
            )}

            {/* Auto-relance cockpit — H+24 / H+48 / J-1 follow-ups */}
            <RelancesPanel />

            {/* Feed columns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {isReferent ? (
                    <>
                        <FeedColumn
                            title="Immersions à évaluer"
                            icon={MapPin}
                            accent="orange"
                            candidates={feed?.immersions ?? []}
                            total={overview?.immersions_upcoming ?? 0}
                            testId="feed-immersions"
                            to="/candidats?status=immersion_scheduled"
                        />
                        <FeedColumn
                            title="Mes recommandations"
                            icon={CheckCircle2}
                            accent="orange"
                            candidates={feed?.validations ?? []}
                            total={overview?.awaiting_validation ?? 0}
                            testId="feed-validations"
                            to="/candidats?status=immersion_done"
                        />
                        <FeedColumn
                            title="Profils à risque"
                            icon={AlertTriangle}
                            accent="red"
                            candidates={feed?.at_risk ?? []}
                            total={overview?.at_risk ?? 0}
                            testId="feed-at-risk"
                            to="/alertes"
                        />
                    </>
                ) : (
                    <>
                        <FeedColumn
                            title="Pépites"
                            icon={Flame}
                            accent="orange"
                            candidates={feed?.pepites ?? []}
                            total={overview?.pepites ?? 0}
                            testId="feed-pepites"
                            to="/pepites"
                        />
                        <FeedColumn
                            title="Appels urgents"
                            icon={Zap}
                            accent="orange"
                            candidates={feed?.urgent ?? []}
                            total={urgentCount}
                            testId="feed-urgent"
                            to="/candidats?status=urgent"
                        />
                        <FeedColumn
                            title="Profils à risque"
                            icon={AlertTriangle}
                            accent="red"
                            candidates={feed?.at_risk ?? []}
                            total={overview?.at_risk ?? 0}
                            testId="feed-at-risk"
                            to="/alertes"
                        />
                    </>
                )}
            </div>

            {/* Pipeline */}
            <div className="card-soft p-5 md:p-7" data-testid="dashboard-pipeline">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-extrabold tracking-tight">Pipeline en cours</h3>
                    <Link to="/candidats" className="btn-ghost text-xs">
                        Voir tout
                    </Link>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                    <div>
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                            Immersions à venir
                        </h4>
                        <div className="space-y-1">
                            {(feed?.immersions ?? []).map((c) => (
                                <MiniCandidateRow key={c.id} candidate={c} />
                            ))}
                            {!feed?.immersions?.length && <EmptyRow label="Aucune immersion planifiée" />}
                        </div>
                    </div>
                    <div>
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                            Validations recrutement
                        </h4>
                        <div className="space-y-1">
                            {(feed?.validations ?? []).map((c) => (
                                <MiniCandidateRow key={c.id} candidate={c} />
                            ))}
                            {!feed?.validations?.length && <EmptyRow label="Aucune validation en attente" />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function offerChip(c) {
    if (!c?.layan_offer_title) return null;
    // Compact label : take first 2 significant words
    const words = c.layan_offer_title
        .replace(/[-–]/g, " ")
        .split(/\s+/)
        .filter((w) => w.length > 2 && !/^(de|du|la|le|les|pour|et|en|f\/h)$/i.test(w));
    const compact = words.slice(0, 2).join(" ") || c.layan_offer_title.slice(0, 20);
    return compact;
}

function FeedColumn({ title, icon: Icon, accent, candidates, testId, to, total }) {
    const displayTotal = total !== undefined ? total : candidates.length;
    const showMoreCount = displayTotal - candidates.length;
    return (
        <div className="card-soft p-5 md:p-6" data-testid={testId}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div
                        className={`h-8 w-8 rounded-xl flex items-center justify-center ${
                            accent === "red"
                                ? "bg-red-50 text-red-600"
                                : "bg-shariti-orange-soft text-shariti-orange"
                        }`}
                    >
                        <Icon className="h-4 w-4" strokeWidth={2.4} />
                    </div>
                    <h3 className="text-sm font-extrabold tracking-tight">{title}</h3>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-base font-black ${accent === "red" ? "text-shariti-red" : "text-shariti-orange"}`}>
                        {displayTotal}
                    </span>
                    {to ? (
                        <Link to={to} className="btn-ghost text-[11px] py-1 px-2">
                            <ArrowRight className="h-3 w-3" />
                        </Link>
                    ) : null}
                </div>
            </div>
            <div className="space-y-2">
                {candidates
                    .filter((c) => c.status !== "rejected" && !c.set_aside)
                    .slice(0, 5).map((c) => {
                    const phone = toE164(c.phone);
                    const signal = liveSignal(c);
                    const chip = offerChip(c);
                    const sourceLabel = c.source === "Site Shariti"
                        ? "Site Shariti"
                        : (c.source && c.source.startsWith("Layan")) ? "Layan" : null;
                    return (
                        <div
                            key={c.id}
                            className="rounded-2xl p-2.5 hover:bg-shariti-gray transition group"
                        >
                            {/* Row 1 : name (full, can wrap) + score + call */}
                            <div className="flex items-start gap-2.5">
                                <Link to={`/candidats/${c.id}`} className="flex items-start gap-2.5 flex-1 min-w-0">
                                    {c.avatar_url ? (
                                        <img src={c.avatar_url} alt="" className="h-9 w-9 rounded-xl object-cover shrink-0" />
                                    ) : (
                                        <div className="h-9 w-9 rounded-xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-sm flex items-center justify-center shrink-0">
                                            {c.first_name?.[0]}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div
                                            className="text-sm font-extrabold text-shariti-black leading-tight break-words"
                                            title={`${c.first_name} ${c.last_name}`}
                                        >
                                            {c.first_name} {c.last_name}
                                        </div>
                                        <div className="mt-0.5 text-xs font-semibold text-slate-500 tabular-nums">
                                            {c.phone}
                                        </div>
                                    </div>
                                    <div
                                        className={`text-base font-black shrink-0 ${
                                            accent === "red" ? "text-shariti-red" : "text-shariti-orange"
                                        }`}
                                    >
                                        {c.ai_score || "—"}
                                    </div>
                                </Link>
                                <a
                                    href={`tel:${phone}`}
                                    title="Appeler"
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-shariti-orange text-white hover:bg-[#d66528] transition shadow-btn shrink-0"
                                >
                                    <Phone className="h-3.5 w-3.5" />
                                </a>
                            </div>
                            {/* Row 2 : source + offer chip + signal */}
                            {(sourceLabel || chip || signal) && (
                                <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-11">
                                    {sourceLabel === "Site Shariti" && (
                                        <span
                                            data-testid={`mini-source-site-${c.id}`}
                                            className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-orange-soft border border-shariti-orange/30 text-shariti-orange shrink-0"
                                        >
                                            🌐 Site Shariti
                                        </span>
                                    )}
                                    {sourceLabel === "Layan" && (
                                        <span
                                            data-testid={`mini-source-layan-${c.id}`}
                                            className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300 text-slate-700 shrink-0"
                                        >
                                            <Briefcase className="h-2.5 w-2.5" /> Layan
                                        </span>
                                    )}
                                    {chip && (
                                        <Link
                                            to={`/candidats?offer_id=${c.layan_offer_id}`}
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`mini-offer-${c.id}`}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-shariti-cream text-slate-600 border border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange transition max-w-full truncate"
                                            title={c.layan_offer_title}
                                        >
                                            <Briefcase className="h-3 w-3 shrink-0" />
                                            <span className="truncate">{chip}</span>
                                        </Link>
                                    )}
                                    {signal && (
                                        <span
                                            data-testid={`mini-signal-${c.id}`}
                                            className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
                                                signal.tone === "alert"
                                                    ? "bg-red-50 text-shariti-red border border-red-200"
                                                    : signal.tone === "warn"
                                                    ? "bg-amber-50 text-amber-800 border border-amber-200"
                                                    : signal.tone === "ok"
                                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                    : "text-slate-500"
                                            }`}
                                        >
                                            {signal.label}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {!candidates.length && <EmptyRow label="Rien à signaler" />}
                {showMoreCount > 0 && (
                    <Link
                        to={to || "/candidats"}
                        className="block text-center py-2 text-[11px] font-bold uppercase tracking-widest text-shariti-orange hover:text-[#d66528] transition"
                    >
                        +{showMoreCount} autre{showMoreCount > 1 ? "s" : ""} →
                    </Link>
                )}
            </div>
        </div>
    );
}

function EmptyRow({ label }) {
    return (
        <div className="text-xs font-semibold text-slate-400 italic px-3 py-4 rounded-2xl bg-shariti-cream/60">
            {label}
        </div>
    );
}
